// features/ApprovalRequest/ApprovalRequestListView/store.ts
import { makeAutoObservable, runInAction } from "mobx";
import i18n from "i18next";
import MainStore from "MainStore";
import dayjs, { Dayjs } from 'dayjs';
import {
  getApprovalRequests,
  deleteApprovalRequest,
  searchApprovalRequests
} from "api/ApprovalRequest";
import {
  getExecutorsByApprovalRequestId,
  getAllOrgUsers,
  createExecutor,
  deleteExecutor
} from "api/approval_request_executor";

import { getstatuses } from "api/status"

interface FilterParams {
  bga_application_number: string;
  applicant_name: string;
  priority: number;
  operator_id: number;
  status_id: number;
  received_date_from: Dayjs | null;
  received_date_to: Dayjs | null;
}

class ApprovalRequestListStore {
  data = [];
  mainData = [];
  openPanel = false;
  currentId = 0;
  searchField = "";
  operators = [];
  priorities = [];
  statuses = [];


  // Диалог исполнителей
  executorDialogOpen = false;
  executorDialogRequestId: number | null = null;
  executorDialogExecutors = [];
  executorAllUsers = [];
  executorUsersLoaded = false;

  filterParams: FilterParams = {
    bga_application_number: '',
    applicant_name: '',
    priority: 0,
    operator_id: 0,
    status_id: 0,
    received_date_from: null,
    received_date_to: null
  };

  constructor() {
    makeAutoObservable(this);
  }

  onEditClicked(id: number) {
    runInAction(() => {
      this.openPanel = true;
      this.currentId = id;
    });
  }

  changeSearch = (value: string) => {
    this.searchField = value;
  }

  onSearchClicked = () => {
    const data = this.mainData.filter(x =>
      x?.bga_application_number?.toLowerCase().includes(this.searchField?.toLowerCase())
      || x?.applicant_name?.toLowerCase().includes(this.searchField?.toLowerCase())
      || x?.approval_type?.toLowerCase().includes(this.searchField?.toLowerCase())
      || x?.current_status?.toLowerCase().includes(this.searchField?.toLowerCase())
    );
    this.data = data;
  }

  clearSearch() {
    this.searchField = "";
    this.loadApprovalRequests();
  }

  updateFilterParam = <K extends keyof FilterParams>(field: K, value: FilterParams[K]) => {
    runInAction(() => {
      this.filterParams[field] = value;
    });
  }

  applyFilter = async () => {
    try {
      MainStore.changeLoader(true);

      // Подготовка параметров для API
      const params: any = {};

      if (this.filterParams.bga_application_number) {
        params.bga_application_number = this.filterParams.bga_application_number;
      }
      if (this.filterParams.applicant_name) {
        params.applicant_name = this.filterParams.applicant_name;
      }
      if (this.filterParams.priority && this.filterParams.priority !== 0) {
        params.priority = this.priorities.find(x => x.id == this.filterParams.priority).name;
      }
      if (this.filterParams.status_id && this.filterParams.status_id !== 0) {
        params.status = this.statuses.find(x => x.id == this.filterParams.status_id).code;
      }
      if (this.filterParams.operator_id && this.filterParams.operator_id !== 0) {
        params.operator_id = this.filterParams.operator_id;
      }
      if (this.filterParams.received_date_from) {
        params.received_date_from = this.filterParams.received_date_from.format('YYYY-MM-DD');
      }
      if (this.filterParams.received_date_to) {
        params.received_date_to = this.filterParams.received_date_to.format('YYYY-MM-DD');
      }

      const response = await searchApprovalRequests(params);

      if ((response.status === 201 || response.status === 200) && response?.data !== null) {
        runInAction(() => {
          this.data = response.data;
          this.mainData = response.data;
        });
      } else {
        throw new Error();
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  }

  clearFilter = () => {
    runInAction(() => {
      this.filterParams = {
        bga_application_number: '',
        applicant_name: '',
        priority: 0,
        operator_id: 0,
        status_id: 0,
        received_date_from: null,
        received_date_to: null
      };
    });
    this.loadApprovalRequests();
  }

  closePanel() {
    runInAction(() => {
      this.openPanel = false;
      this.currentId = 0;
    });
  }

  // Mock данные для приоритетов - замените на реальный API вызов
  loadPriorities = async () => {
    try {
      // TODO: Заменить на реальный API вызов
      // const response = await getPriorities();
      // if ((response.status === 201 || response.status === 200) && response?.data !== null) {
      //   runInAction(() => {
      //     this.priorities = response.data;
      //   });
      // }

      // Mock данные
      runInAction(() => {
        this.priorities = [
          { id: 1, name: 'Стандартный' },
          { id: 2, name: 'Высокий' },
          { id: 3, name: 'Наивысший' }
        ];
      });
    } catch (err) {
      console.error("Error loading priorities:", err);
    }
  }

  // Mock данные для операторов - замените на реальный API вызов
  loadOperators = async () => {
    try {
      // TODO: Заменить на реальный API вызов
      // const response = await getOperators();
      // if ((response.status === 201 || response.status === 200) && response?.data !== null) {
      //   runInAction(() => {
      //     this.operators = response.data;
      //   });
      // }

      // Mock данные
      runInAction(() => {
        this.operators = [
          { id: 1, name: 'Оператор 1' },
          { id: 2, name: 'Оператор 2' },
          { id: 3, name: 'Оператор 3' },
          { id: 4, name: 'Оператор 4' }
        ];
      });
    } catch (err) {
      console.error("Error loading operators:", err);
    }
  }

  loadStatuses = async () => {
    try {
      // TODO: Заменить на реальный API вызов
      const response = await getstatuses();
      if ((response.status === 201 || response.status === 200) && response?.data !== null) {
        runInAction(() => {
          this.statuses = response.data;
        });
      }
    } catch (err) {
      console.error("Error loading operators:", err);
    }
  }

  loadApprovalRequests = async () => {
    try {
      MainStore.changeLoader(true);
      const response = await getApprovalRequests();
      if ((response.status === 201 || response.status === 200) && response?.data !== null) {
        runInAction(() => {
          this.data = response.data;
          this.mainData = response.data;
        });
      } else {
        throw new Error();
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  };

  deleteApprovalRequest = (id: number) => {
    MainStore.openErrorConfirm(
      i18n.t("areYouSure"),
      i18n.t("delete"),
      i18n.t("no"),
      async () => {
        try {
          MainStore.changeLoader(true);
          const response = await deleteApprovalRequest(id);
          if (response.status === 201 || response.status === 200) {
            this.loadApprovalRequests();
            MainStore.setSnackbar(i18n.t("message:snackbar.successDelete"));
          } else {
            throw new Error();
          }
        } catch (err) {
          MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
        } finally {
          MainStore.changeLoader(false);
          MainStore.onCloseConfirm();
        }
      },
      () => MainStore.onCloseConfirm()
    );
  };

  openExecutorDialog = async (requestId: number) => {
    this.executorDialogRequestId = requestId;
    this.executorDialogOpen = true;
    await Promise.all([
      this.loadExecutors(requestId),
      this.executorUsersLoaded ? Promise.resolve() : this.loadAllExecutorUsers(),
    ]);
  };

  closeExecutorDialog = () => {
    this.executorDialogOpen = false;
    this.executorDialogRequestId = null;
    this.executorDialogExecutors = [];
  };

  loadExecutors = async (requestId: number) => {
    try {
      const response = await getExecutorsByApprovalRequestId(requestId);
      if ((response.status === 200 || response.status === 201) && response?.data) {
        runInAction(() => { this.executorDialogExecutors = response.data; });
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    }
  };

  loadAllExecutorUsers = async () => {
    try {
      const response = await getAllOrgUsers();
      if ((response.status === 200 || response.status === 201) && response?.data) {
        runInAction(() => {
          this.executorAllUsers = response.data;
          this.executorUsersLoaded = true;
        });
      }
    } catch (err) {
      // ignore
    }
  };

  addExecutor = async (userAccountId: number) => {
    if (!this.executorDialogRequestId) return;
    try {
      MainStore.changeLoader(true);
      await createExecutor({ approval_request_id: this.executorDialogRequestId, user_account_id: userAccountId });
      await this.loadExecutors(this.executorDialogRequestId);
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  };

  removeExecutor = async (executorId: number) => {
    if (!this.executorDialogRequestId) return;
    try {
      MainStore.changeLoader(true);
      const response = await deleteExecutor(executorId);
      if (response.status === 200 || response.status === 201) {
        await this.loadExecutors(this.executorDialogRequestId);
      } else {
        const msg = response.data?.message || i18n.t("message:somethingWentWrong");
        MainStore.setSnackbar(msg, "error");
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || i18n.t("message:somethingWentWrong");
      MainStore.setSnackbar(msg, "error");
    } finally {
      MainStore.changeLoader(false);
    }
  };

  clearStore = () => {
    runInAction(() => {
      this.data = [];
      this.currentId = 0;
      this.openPanel = false;
      this.searchField = "";
      this.operators = [];
      this.priorities = [];
      this.executorDialogOpen = false;
      this.executorDialogRequestId = null;
      this.executorDialogExecutors = [];
      this.executorAllUsers = [];
      this.executorUsersLoaded = false;
      this.filterParams = {
        bga_application_number: '',
        applicant_name: '',
        priority: 0,
        status_id: 0,
        operator_id: 0,
        received_date_from: null,
        received_date_to: null
      };
    });
  };
}

export default new ApprovalRequestListStore();